"use client";

import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from "@/lib/i18n/hook";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";
export function StepIncomeExpense() {
  const { control } = useFormContext();
  const { t } = useI18n();
  const { currency } = useCurrencyContext();
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">
        {t("lifePlan.incomeExpense.title")}
      </h2>

      <Tabs defaultValue="income" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="income">
            {t("lifePlan.incomeExpense.income.title")}
          </TabsTrigger>
          <TabsTrigger value="expense">
            {t("lifePlan.incomeExpense.expense.title")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="income" className="mt-4 space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <FormField
                control={control}
                name="income.annualIncome"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>
                        {t("lifePlan.incomeExpense.income.annualIncome")}
                      </FormLabel>
                    </div>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          min={0}
                          value={field.value}
                          onValueChange={(val) => field.onChange(val ?? 0)}
                          ref={field.ref}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <span className="text-sm">
                        {currency}
                        {t("common.perYear")}
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="income.salaryIncreaseRate"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>
                        {t("lifePlan.incomeExpense.income.salaryIncreaseRate")}
                      </FormLabel>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Slider
                            min={0}
                            max={10}
                            step={0.1}
                            value={[field.value]}
                            onValueChange={(value) => field.onChange(value[0])}
                            className="w-full"
                          />
                        </FormControl>
                        <span className="w-12 text-right">{field.value}%</span>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="income.annualBonus"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>
                        {t("lifePlan.incomeExpense.income.annualBonus")}
                      </FormLabel>
                    </div>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          min={0}
                          value={field.value}
                          onValueChange={(val) => field.onChange(val ?? 0)}
                          ref={field.ref}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <span className="text-sm">
                        {currency}
                        {t("common.perYear")}
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expense" className="mt-4 space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <FormField
                control={control}
                name="expense.housingCost"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.housingCost")}
                      </FormLabel>
                    </div>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          min={0}
                          value={field.value}
                          onValueChange={(val) => field.onChange(val ?? 0)}
                          ref={field.ref}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <span className="text-sm">
                        {currency}
                        {t("common.perMonth")}
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="expense.utilities"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.utilities")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="expense.food"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.food")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="expense.communication"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.communication")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="expense.transportation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.transportation")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="expense.entertainment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.entertainment")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="expense.other"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.other")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium">
                  {t("lifePlan.incomeExpense.expense.investment")}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={control}
                  name="investment.stocks"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.stocks")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="investment.bonds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.bonds")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="investment.mutualFunds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.mutualFunds")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="investment.insurance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.insurance")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="investment.other"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.incomeExpense.expense.other")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">
                          {currency}
                          {t("common.perMonth")}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
